import { useState, useEffect, useCallback, useRef } from 'react';
import { Heart, ChevronLeft, ChevronRight, Menu, X, ArrowRight, ExternalLink } from 'lucide-react';

// Helper function to generate correct image paths
const getImagePath = (path) => {
  // Remove leading slash if present
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  
  // Check if we're in GitHub Pages environment
  const isGitHubPages = window.location.hostname.includes('github.io');
  
  if (isGitHubPages) {
    // For GitHub Pages, include the repository name in the path
    return `/MiaBelovedMausi/${cleanPath}`;
  } else {
    // For local development and other deployments
    return `/${cleanPath}`;
  }
};

// Enhanced gallery data with AI prompts and metadata
const galleries = {
  goldenHour: {
    title: "Golden Hour Warriors",
    description: "Cinematic scenes of battle-worn Nordic warriors at golden hour",
    images: Array.from({ length: 10 }, (_, i) => ({
      src: getImagePath(`images/Mausi&Jagiya/01-golden-hour-warrior/warrior-golden-hour-${String(i + 1).padStart(2, '0')}.jpeg`),
      caption: `In the golden light of destiny, a warrior's heart finds peace`,
      prompt: `AI-generated portrait featuring a Nordic warrior in golden hour lighting. Created using Stable Diffusion with custom LoRA models for cinematic composition, warm color grading, and dramatic backlighting. Post-processed for enhanced glow effects and color harmony.`
    }))
  },
  fantasyRomance: {
    title: "Fantasy Romance",
    description: "Surreal anime-inspired romantic fantasy scenes",
    images: [
      ...Array.from({ length: 4 }, (_, i) => ({
        src: getImagePath(`images/Mausi&Jagiya/02-fantasy-romance/nordic-warrior-romance-${String(i + 1).padStart(2, '0')}.jpeg`),
        caption: `Dreamy romantic fantasy scene ${i + 1}`,
        prompt: `Dreamy, ethereal scenes blending fantasy and romance. Created with Midjourney v6 using advanced stylization parameters for enhanced artistic interpretation.`
      })),
      ...Array.from({ length: 3 }, (_, i) => ({
        src: getImagePath(`images/Mausi&Jagiya/02-fantasy-romance/nordic-warrior-fantasy-${String(i + 1).padStart(2, '0')}.jpeg`),
        caption: `Fantasy warrior scene ${i + 1}`,
        prompt: `Fantasy warrior portraits with ethereal lighting and magical ambiance. Enhanced with custom post-processing for dreamlike quality.`
      })),
      ...Array.from({ length: 4 }, (_, i) => ({
        src: getImagePath(`images/Mausi&Jagiya/02-fantasy-romance/nordic-warrior-surreal-${String(i + 1).padStart(2, '0')}.jpeg`),
        caption: `Surreal warrior portrait ${i + 1}`,
        prompt: `Surreal Nordic warrior portraits with artistic interpretation and enhanced visual storytelling elements.`
      })),
      ...Array.from({ length: 6 }, (_, i) => ({
        src: getImagePath(`images/Mausi&Jagiya/02-fantasy-romance/nordic-warrior-depicting-${String(i + 1).padStart(2, '0')}.jpeg`),
        caption: `Depicting warrior essence ${i + 1}`,
        prompt: `Artistic depictions of Nordic warriors with emphasis on emotional depth and visual narrative.`
      }))
    ]
  },
  enchantedSprings: {
    title: "Enchanted Springs",
    description: "Magical hot spring scenes surrounded by ethereal beauty",
    images: Array.from({ length: 4 }, (_, i) => ({
      src: getImagePath(`images/Mausi&Jagiya/03-enchanted-springs/enchanted-spring-${String(i + 1).padStart(2, '0')}.jpeg`),
      caption: `Tranquil waters of enchantment ${i + 1}`,
      prompt: `Tranquil water scenes with magical ambiance. Post-processed for enhanced glow effects and color harmony with ethereal lighting.`
    }))
  },
  hopefulMoments: {
    title: "Hopeful Moments",
    description: "Gentle scenes of devotion and hope at golden hour",
    images: Array.from({ length: 3 }, (_, i) => ({
      src: getImagePath(`images/Mausi&Jagiya/04-hopeful-moments/hopeful-viking-kneeling-${String(i + 1).padStart(2, '0')}.jpeg`),
      caption: `Intimate portraits capturing vulnerability and devotion ${i + 1}`,
      prompt: `Intimate portraits capturing vulnerability and devotion. Each frame tells a story of commitment and hope with golden hour lighting.`
    }))
  },
  emotionalJourney: {
    title: "Emotional Journey",
    description: "A sequential portrait collection capturing the evolution of emotion",
    images: Array.from({ length: 13 }, (_, i) => ({
      src: getImagePath(`images/myMia/01-emotional-journey/mia-portrait-journey-${String(i + 1).padStart(2, '0')}.jpeg`),
      caption: `Emotional journey portrait ${i + 1}`,
      prompt: `Sequential portrait collection capturing emotional evolution with photorealistic detail and artistic composition.`
    }))
  },
  kpopIdol: {
    title: "K-Pop Idol Energy",
    description: "Stunning photorealistic portraits capturing vibrant K-pop idol aesthetics",
    images: Array.from({ length: 13 }, (_, i) => ({
      src: getImagePath(`images/k-pop/01-kpop-idol-portraits/kpop-idol-portrait-${String(i + 1).padStart(2, '0')}.jpeg`),
      caption: `K-Pop idol portrait ${i + 1}`,
      prompt: `Stunning photorealistic portraits capturing vibrant K-pop idol aesthetics with professional lighting and styling.`
    }))
  },
  cyberpunkWarriors: {
    title: "Cyberpunk Warriors",
    description: "Epic Nordic cyborg warriors with Viking flame tattoos in dystopian battlefields",
    images: [
      // Images 01-21 exist
      ...Array.from({ length: 21 }, (_, i) => ({
        src: getImagePath(`images/cyberpunk/01-nordic-cyborg-warriors/nordic-cyborg-warrior-${String(i + 1).padStart(2, '0')}.jpeg`),
        caption: `Cyberpunk warrior ${i + 1}`,
        prompt: `Epic Nordic cyborg warriors with Viking flame tattoos in dystopian battlefields. Created with advanced AI techniques for futuristic aesthetics.`
      })),
      // Images 30-49 exist (skip 22-29 which are missing)
      ...Array.from({ length: 20 }, (_, i) => ({
        src: getImagePath(`images/cyberpunk/01-nordic-cyborg-warriors/nordic-cyborg-warrior-${String(i + 30).padStart(2, '0')}.jpeg`),
        caption: `Cyberpunk warrior ${i + 30}`,
        prompt: `Epic Nordic cyborg warriors with Viking flame tattoos in dystopian battlefields. Created with advanced AI techniques for futuristic aesthetics.`
      }))
    ]
  },
  inspirationalMessages: {
    title: "Inspirational Messages",
    description: "Heartfelt messages of love, strength, and moving forward together",
    images: [
      {
        src: getImagePath(`images/inspirational/01-motivational-messages/leave-our-demons-behind-01.jpg`),
        caption: "Let's leave our demons behind and embrace our future together",
        prompt: "Inspirational message design with motivational typography and emotional visual elements."
      },
      {
        src: getImagePath(`images/inspirational/01-motivational-messages/mausi-jagiya-message-02.jpg`),
        caption: "A personal message from Jagiya to Mausi, expressing eternal love",
        prompt: "Personal love message with custom typography and romantic visual design elements."
      },
      {
        src: getImagePath(`images/inspirational/01-motivational-messages/my-strength-03.jpg`),
        caption: "You are my strength, my inspiration, my everything",
        prompt: "Strength and inspiration message with powerful typography and uplifting visual composition."
      }
    ]
  }
};

// Enhanced daily highlights with seeded randomization
const getDailyHighlights = () => {
  const allImages = Object.values(galleries).flatMap(g => g.images);
  const today = new Date();
  const dayOfYear = Math.floor((today - new Date(today.getFullYear(), 0, 0)) / 86400000);
  
  // Seeded random function
  const seededRandom = (seed) => {
    const x = Math.sin(seed) * 10000;
    return x - Math.floor(x);
  };
  
  // Shuffle using seed
  const shuffled = [...allImages].sort((a, b) => {
    const indexA = allImages.indexOf(a);
    const indexB = allImages.indexOf(b);
    return seededRandom(dayOfYear + indexA) - seededRandom(dayOfYear + indexB);
  });
  
  return {
    featured: shuffled[0],
    weekly: shuffled.slice(1, 7) // Skip the first image (featured) and get next 6 for clean 2x3 grid
  };
};

// Enhanced 3D Floating Hearts with champagne bubble physics
const FloatingHearts = () => {
  const [hearts, setHearts] = useState([]);

  useEffect(() => {
    const createHeart = () => {
      const id = Date.now() + Math.random();
      const heartEmojis = ['💖', '💕', '💗', '💓', '💝', '💘', '💞', '💟', '❤️', '🧡', '💛', '💚', '💙', '💜', '🤍', '🖤', '❣️', '💋'];
      const newHeart = {
        id,
        emoji: heartEmojis[Math.floor(Math.random() * heartEmojis.length)],
        left: Math.random() * 100,
        size: 16 + Math.random() * 32,
        duration: 8 + Math.random() * 6,
        delay: Math.random() * 3,
        rotation: Math.random() * 360,
        drift: (Math.random() - 0.5) * 30, // Horizontal drift like champagne bubbles
        opacity: 0.6 + Math.random() * 0.4
      };
      
      setHearts(prev => [...prev, newHeart]);
      
      // Remove heart after animation
      setTimeout(() => {
        setHearts(prev => prev.filter(h => h.id !== id));
      }, (newHeart.duration + newHeart.delay) * 1000);
    };

    // Create initial hearts
    for (let i = 0; i < 20; i++) {
      setTimeout(createHeart, i * 150);
    }

    // Continue creating hearts
    const interval = setInterval(createHeart, 1500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
      {hearts.map(heart => (
        <div
          key={heart.id}
          className="absolute animate-float-up"
          style={{
            left: `${heart.left}%`,
            bottom: '-50px',
            fontSize: `${heart.size}px`,
            animationDuration: `${heart.duration}s`,
            animationDelay: `${heart.delay}s`,
            transform: `rotate(${heart.rotation}deg)`,
            opacity: heart.opacity,
            filter: 'drop-shadow(0 0 10px rgba(236, 72, 153, 0.5))',
            '--drift': `${heart.drift}px`
          }}
        >
          {heart.emoji}
        </div>
      ))}
    </div>
  );
};

// Cupid Arrows Animation
const CupidArrows = () => {
  const [arrows, setArrows] = useState([]);

  useEffect(() => {
    const createArrow = () => {
      if (Math.random() > 0.3) return; // 70% chance
      
      const id = Date.now() + Math.random();
      const newArrow = {
        id,
        top: 20 + Math.random() * 60,
        duration: 3 + Math.random() * 2
      };
      
      setArrows(prev => [...prev, newArrow]);
      
      setTimeout(() => {
        setArrows(prev => prev.filter(a => a.id !== id));
      }, newArrow.duration * 1000);
    };

    const interval = setInterval(createArrow, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-10">
      {arrows.map(arrow => (
        <div
          key={arrow.id}
          className="absolute text-pink-400/60 animate-arrow-fly"
          style={{
            left: '-50px',
            top: `${arrow.top}%`,
            animationDuration: `${arrow.duration}s`
          }}
        >
          <ArrowRight size={24} />
        </div>
      ))}
    </div>
  );
};

// Floating Hearts Mini-Game Component
const FloatingHeartsGame = ({ isActive, onGameComplete, onGameExit }) => {
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState([]);
  const [missedClicks, setMissedClicks] = useState(0);
  const [gameState, setGameState] = useState('playing');
  const [showInstructions, setShowInstructions] = useState(true);
  const [gameHearts, setGameHearts] = useState([]);
  const [completionHandled, setCompletionHandled] = useState(false);

  // Performance-optimized refs
  const gameContainerRef = useRef(null);
  const animationFrameRef = useRef(null);
  const heartsRef = useRef([]);
  const lastSpawnTimeRef = useRef(0);
  const performanceRef = useRef({ frameCount: 0, lastTime: 0, fps: 60 });
  const heartPoolRef = useRef([]);
  const maxHeartsRef = useRef(6); // Reduced max hearts for better performance

  // Performance monitoring
  const updatePerformance = useCallback((currentTime) => {
    const perf = performanceRef.current;
    perf.frameCount++;
    
    if (currentTime - perf.lastTime >= 1000) {
      perf.fps = perf.frameCount;
      perf.frameCount = 0;
      perf.lastTime = currentTime;
      
      // Adaptive quality scaling
      if (perf.fps < 45) {
        maxHeartsRef.current = Math.max(3, maxHeartsRef.current - 1);
      } else if (perf.fps > 55 && maxHeartsRef.current < 6) {
        maxHeartsRef.current = Math.min(6, maxHeartsRef.current + 1);
      }
    }
  }, []);

  // Simplified heart configs for better performance
  const heartConfigs = [
    { color: 'text-pink-400', name: 'pink', emoji: '💖', basePoints: 1 },
    { color: 'text-red-500', name: 'red', emoji: '❤️', basePoints: 1 },
    { color: 'text-purple-400', name: 'purple', emoji: '💜', basePoints: 1 },
    { color: 'text-yellow-400', name: 'gold', emoji: '💛', basePoints: 2 },
    { color: 'text-blue-400', name: 'blue', emoji: '💙', basePoints: 1 },
    { color: 'text-green-400', name: 'green', emoji: '💚', basePoints: 1 },
    { color: 'text-orange-400', name: 'orange', emoji: '🧡', basePoints: 1 },
    { color: 'text-rose-400', name: 'rose', emoji: '🌹', basePoints: 1 }
  ];

  // Simplified speed tiers
  const speedTiers = [
    { name: 'normal', speed: 1.2, multiplier: 1, probability: 0.6 },
    { name: 'fast', speed: 2.0, multiplier: 2, probability: 0.3 },
    { name: 'very-fast', speed: 3.0, multiplier: 3, probability: 0.1 }
  ];

  // Object pooling for hearts
  const getPooledHeart = useCallback(() => {
    return heartPoolRef.current.pop() || {
      id: Math.random().toString(36).substr(2, 9),
      x: 0, y: 0, rotation: 0, scale: 1,
      clicked: false, spawnTime: 0, lifespan: 5000
    };
  }, []);

  const returnToPool = useCallback((heart) => {
    heart.clicked = false;
    heart.spawnTime = 0;
    heartPoolRef.current.push(heart);
  }, []);

  const getGameBounds = useCallback(() => {
    if (!gameContainerRef.current) return { width: 800, height: 600 };
    const rect = gameContainerRef.current.getBoundingClientRect();
    return { width: rect.width, height: rect.height };
  }, []);

  // Optimized heart spawning
  const spawnHeart = useCallback((currentTime) => {
    if (heartsRef.current.length >= maxHeartsRef.current) return;

    const bounds = getGameBounds();
    const config = heartConfigs[Math.floor(Math.random() * heartConfigs.length)];
    const tier = speedTiers.find(t => Math.random() < t.probability) || speedTiers[0];
    
    const heart = getPooledHeart();
    
    heart.x = Math.random() * (bounds.width - 60);
    heart.y = bounds.height + 20;
    heart.rotation = Math.random() * 360;
    heart.scale = 0.8 + Math.random() * 0.4;
    heart.speed = tier.speed;
    heart.tier = tier.name;
    heart.points = config.basePoints * tier.multiplier;
    heart.spawnTime = currentTime;
    heart.lifespan = 5000;
    heart.color = config.color;
    heart.name = config.name;
    heart.emoji = config.emoji;
    heart.clicked = false;
    
    heartsRef.current.push(heart);
  }, [getPooledHeart, getGameBounds]);

  // Highly optimized animation loop
  const animate = useCallback((currentTime) => {
    if (!isActive || gameState !== 'playing') return;

    updatePerformance(currentTime);

    // Spawn hearts less frequently for better performance
    if (currentTime - lastSpawnTimeRef.current > 1500) { // Increased interval
      spawnHeart(currentTime);
      lastSpawnTimeRef.current = currentTime;
    }

    // Simplified heart movement - no complex calculations
    const activeHearts = [];

    heartsRef.current.forEach(heart => {
      if (heart.clicked) {
        activeHearts.push(heart);
        return;
      }

      // Simple linear movement
      heart.y -= heart.speed;
      heart.rotation += 0.5; // Reduced rotation speed

      // Check if heart is still visible and within lifespan
      const age = currentTime - heart.spawnTime;
      if (heart.y > -60 && age < heart.lifespan) {
        activeHearts.push(heart);
      } else {
        // Heart reached top or expired - no penalty for casual gameplay
        // Hearts simply disappear without counting as a miss
        returnToPool(heart);
      }
    });

    heartsRef.current = activeHearts;

    // Throttled state update - only update every few frames
    if (performanceRef.current.frameCount % 3 === 0) {
      setGameHearts([...heartsRef.current]);
    }

    animationFrameRef.current = requestAnimationFrame(animate);
  }, [isActive, gameState, spawnHeart, getGameBounds, updatePerformance, returnToPool]);

  // Optimized heart click handler
  const handleHeartClick = useCallback((heartId, heartName, points) => {
    const heartIndex = heartsRef.current.findIndex(h => h.id === heartId);
    if (heartIndex === -1) return;

    const heart = heartsRef.current[heartIndex];
    heart.clicked = true;

    setScore(prev => prev + points);
    setStreak(prev => {
      const newStreak = [...prev, heartName];
      
      // Check for win conditions
      if (newStreak.length >= 3) {
        const lastThree = newStreak.slice(-3);
        if (lastThree.every(color => color === lastThree[0])) {
          setTimeout(() => setGameState('won'), 100);
        }
      }
      
      return newStreak;
    });

    // Remove heart after brief delay
    setTimeout(() => {
      const index = heartsRef.current.findIndex(h => h.id === heartId);
      if (index !== -1) {
        returnToPool(heartsRef.current[index]);
        heartsRef.current.splice(index, 1);
      }
    }, 300);
  }, [returnToPool]);

  const handleBackgroundClick = useCallback(() => {
    setMissedClicks(prev => prev + 1);
  }, []);

  const resetGame = useCallback(() => {
    setScore(0);
    setStreak([]);
    setMissedClicks(0);
    setGameState('playing');
    setShowInstructions(true);
    setCompletionHandled(false); // Reset completion handler flag
    
    // Return all hearts to pool
    heartsRef.current.forEach(returnToPool);
    heartsRef.current = [];
    setGameHearts([]);
    
    lastSpawnTimeRef.current = 0;
    maxHeartsRef.current = 6; // Reset max hearts
    performanceRef.current = { frameCount: 0, lastTime: 0, fps: 60 };
  }, [returnToPool]);

  // Animation lifecycle
  useEffect(() => {
    if (isActive && gameState === 'playing') {
      heartsRef.current = [];
      lastSpawnTimeRef.current = 0;
      animationFrameRef.current = requestAnimationFrame(animate);
      
      // Hide instructions after delay
      const timer = setTimeout(() => setShowInstructions(false), 3000);
      
      return () => {
        clearTimeout(timer);
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
      };
    }
  }, [isActive, gameState, animate]);

  // Game state effects
  useEffect(() => {
    if (score >= 8) {
      setGameState('won');
    }
  }, [score]);

  useEffect(() => {
    if (missedClicks >= 5) {
      setGameState('lost');
    }
  }, [missedClicks]);

  useEffect(() => {
    if (gameState === 'won' && !completionHandled) {
      setCompletionHandled(true);
      setTimeout(() => onGameComplete(), 2000);
    }
  }, [gameState, completionHandled, onGameComplete]);

  // Reset completion handler when game becomes active
  useEffect(() => {
    if (isActive) {
      setCompletionHandled(false);
    }
  }, [isActive]);

  if (!isActive) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Optimized Game Area */}
      <div
        ref={gameContainerRef}
        className="game-background absolute inset-0 cursor-crosshair"
        onClick={handleBackgroundClick}
        style={{ willChange: 'transform' }} // Hardware acceleration hint
      >
        {/* Optimized Game Hearts */}
        {gameHearts.map(heart => (
          <div
            key={heart.id}
            className={`absolute cursor-pointer ${heart.color}`}
            style={{
              left: `${heart.x}px`,
              top: `${heart.y}px`,
              fontSize: `${heart.scale * 32}px`, // Simplified sizing
              transform: `translate3d(0, 0, 0) rotate(${heart.rotation}deg) scale(${heart.scale})`, // Hardware acceleration
              willChange: 'transform', // Performance hint
              filter: heart.clicked 
                ? 'brightness(3) saturate(2)' // Simplified effects
                : 'brightness(1.5) saturate(1.5)',
              opacity: heart.clicked ? 0.4 : 1,
              pointerEvents: heart.clicked ? 'none' : 'auto',
              textShadow: heart.clicked 
                ? '0 0 20px currentColor' 
                : '0 0 10px currentColor, 1px 1px 0px rgba(0,0,0,0.8)',
              animation: heart.clicked 
                ? 'heartExplode 0.4s ease-out forwards' 
                : 'heartPulse 2s ease-in-out infinite',
              zIndex: heart.tier === 'very-fast' ? 100 : heart.tier === 'fast' ? 90 : 80,
              WebkitTextStroke: '0.5px rgba(0,0,0,0.5)' // Reduced stroke
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (!heart.clicked) {
                handleHeartClick(heart.id, heart.name, heart.points);
              }
            }}
          >
            {heart.emoji}
            {/* Simplified speed indicator */}
            {heart.tier === 'very-fast' && (
              <div className="absolute -top-1 -right-1 text-xs bg-red-500 text-white rounded-full w-3 h-3 flex items-center justify-center">
                ⚡
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Game UI */}
      <div className="absolute top-6 left-6 right-6 flex justify-between items-start pointer-events-none">
        {/* Score and Progress */}
        <div className="glass-tooltip p-4 rounded-xl pointer-events-auto">
          <div className="text-white space-y-2">
            <div className="text-2xl font-bold drop-shadow-lg">Score: {score}/8</div>
            <div className="text-sm drop-shadow-md">
              Streak: {streak.slice(-3).map((color, i) => (
                <span key={i} className={`inline-block w-4 h-4 rounded-full ml-1 shadow-lg ${
                  color === 'pink' ? 'bg-pink-400' :
                  color === 'red' ? 'bg-red-500' :
                  color === 'purple' ? 'bg-purple-400' :
                  color === 'gold' ? 'bg-yellow-400' :
                  color === 'blue' ? 'bg-blue-400' :
                  color === 'green' ? 'bg-green-400' :
                  color === 'orange' ? 'bg-orange-400' :
                  color === 'rose' ? 'bg-rose-400' :
                  'bg-gray-400'
                }`}></span>
              ))}
            </div>
            <div className="text-xs text-red-400 drop-shadow-md">Misses: {missedClicks}/5</div>
            {/* Performance indicator */}
            <div className="text-xs text-blue-300 drop-shadow-md">
              FPS: {performanceRef.current.fps} | Hearts: {maxHeartsRef.current}
            </div>
          </div>
        </div>

        {/* Exit Button */}
        <button
          onClick={onGameExit}
          className="glass-tooltip p-3 rounded-xl text-white hover:bg-white/20 transition-all pointer-events-auto drop-shadow-lg"
        >
          <X className="w-6 h-6 drop-shadow-md" />
        </button>
      </div>

      {/* Instructions */}
      {showInstructions && (
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 glass-tooltip-strong p-8 rounded-2xl text-center max-w-md pointer-events-none">
          <h3 className="text-2xl font-bold text-pink-400 mb-4 drop-shadow-lg">Enhanced Hearts Challenge!</h3>
          <p className="text-white mb-2 drop-shadow-md">Click the floating hearts to win!</p>
          <p className="text-sm text-white/90 mb-4 drop-shadow-md">
            Win by: <br/>
            • Clicking 8 hearts without missing<br/>
            • OR clicking 3 same-colored hearts in a row<br/>
            <span className="text-yellow-300 drop-shadow-md">⚡ Faster hearts = More points!</span>
          </p>
          <p className="text-xs text-red-400 drop-shadow-md">Missing 5 clicks resets your progress!</p>
          <p className="text-xs text-blue-300 mt-2 drop-shadow-md">Optimized for smooth performance!</p>
        </div>
      )}

      {/* Game Over States */}
      {gameState === 'won' && (
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 glass-tooltip-strong p-8 rounded-2xl text-center pointer-events-auto">
          <h3 className="text-3xl font-bold text-pink-400 mb-4 drop-shadow-lg">🎉 Victory! 🎉</h3>
          <p className="text-white mb-4 drop-shadow-md">You've unlocked the kitten video!</p>
          <p className="text-sm text-white/90 drop-shadow-md">Preparing your reward...</p>
        </div>
      )}

      {gameState === 'lost' && (
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 glass-tooltip-strong p-8 rounded-2xl text-center pointer-events-auto">
          <h3 className="text-3xl font-bold text-red-400 mb-4 drop-shadow-lg">Game Over</h3>
          <p className="text-white mb-4 drop-shadow-md">Too many misses! Try again?</p>
          <div className="flex gap-4 justify-center">
            <button
              onClick={resetGame}
              className="glass-tooltip px-6 py-2 rounded-lg text-white hover:bg-white/20 transition-all drop-shadow-md"
            >
              Try Again
            </button>
            <button
              onClick={onGameExit}
              className="glass-tooltip px-6 py-2 rounded-lg text-white hover:bg-white/20 transition-all drop-shadow-md"
            >
              Exit
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Video Modal Component for Easter Egg
const VideoModal = ({ isOpen, onClose, videoId, customTitle, customDescription }) => {
  const [showExplosion, setShowExplosion] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      setShowExplosion(true);
      setIsLoading(true);
      const timer = setTimeout(() => setShowExplosion(false), 1000);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape, true);
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape, true);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const handleVideoLoad = () => {
    setIsLoading(false);
  };

  const handleBackdropClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onClose();
  };

  const handleCloseClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={handleBackdropClick}>
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-xl"
        onClick={handleBackdropClick}
      />
      
      {/* Heart Explosion */}
      {showExplosion && (
        <div className="absolute inset-0 pointer-events-none">
          {Array.from({ length: 30 }).map((_, i) => (
            <Heart
              key={i}
              className="absolute text-pink-500 animate-heart-explosion"
              style={{
                left: '50%',
                top: '50%',
                fontSize: '24px',
                animationDelay: `${i * 0.05}s`,
                '--random-x': `${(Math.random() - 0.5) * 400}px`,
                '--random-y': `${(Math.random() - 0.5) * 400}px`
              }}
            />
          ))}
        </div>
      )}
      
      {/* Modal Content */}
      <div className="relative glass-strong rounded-2xl p-6 w-full max-w-4xl max-h-[90vh] overflow-hidden animate-scale-in" onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        <button
          type="button"
          onClick={handleCloseClick}
          className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full glass-light hover:glass-strong transition-all flex items-center justify-center text-white hover:text-pink-400"
        >
          <X size={20} />
        </button>
        
        {/* Video Container */}
        <div className="relative w-full aspect-video rounded-xl overflow-hidden bg-black/50">
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/70 z-10">
              <div className="text-center space-y-4">
                <Heart className="w-12 h-12 mx-auto text-pink-400 animate-heartbeat" />
                <p className="text-white/80">Loading surprise video...</p>
              </div>
            </div>
          )}
          
          <iframe
            src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`}
            title="Easter Egg Video"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            sandbox="allow-scripts allow-same-origin allow-presentation"
            className="w-full h-full"
            onLoad={handleVideoLoad}
          />
        </div>
        
        {/* Video Title */}
        <div className="mt-4 text-center">
          <h3 className="text-2xl font-serif font-bold text-pink-400 mb-2">
            {customTitle || "🦊 Kitsune's Secret Video! 🦊"}
          </h3>
          <p className="text-white/70">
            {customDescription || "You found the Easter Egg! Enjoy this special surprise 💖"}
          </p>
        </div>
      </div>
    </div>
  );
};


// Love Letter Modal with Heart Explosion
const LoveLetterModal = ({ isOpen, onClose }) => {
  const [showExplosion, setShowExplosion] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShowExplosion(true);
      const timer = setTimeout(() => setShowExplosion(false), 1000);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-md"
        onClick={(e) => {
          e.preventDefault();
          onClose();
        }}
      />
      
      {/* Heart Explosion */}
      {showExplosion && (
        <div className="absolute inset-0 pointer-events-none">
          {Array.from({ length: 20 }).map((_, i) => (
            <Heart
              key={i}
              className="absolute text-pink-500 animate-heart-explosion"
              style={{
                left: '50%',
                top: '50%',
                fontSize: '24px',
                '--explosion-angle': `${(i * 18)}deg`,
                animationDelay: `${i * 0.05}s`
              }}
            />
          ))}
        </div>
      )}
      
      {/* Modal Content */}
      <div className="relative max-w-4xl w-full max-h-[90vh] overflow-y-auto glass rounded-3xl p-8 animate-modal-appear">
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            onClose();
          }}
          className="absolute top-4 right-4 glass p-2 rounded-full hover:bg-white/20 transition-colors"
        >
          <X size={24} />
        </button>
        
        <div className="space-y-8">
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-center text-gradient mb-12">
            My Dearest Mia
          </h2>
          
          <div className="prose prose-lg prose-invert max-w-none space-y-6 text-white/90 leading-relaxed">
            <p className="text-xl first-letter:text-7xl first-letter:font-serif first-letter:text-pink-400 first-letter:float-left first-letter:mr-3 first-letter:mt-1">
              My Beloved Mia Mausi,
            </p>

            <p className="text-lg">
              I once heard these words: "The road of excess leads to the palace of wisdom—for how does one know what is enough, until one knows what is more than enough?" For years, I carried them like a compass, believing I'd found my north star. I was right about their truth, just catastrophically wrong about how to follow them.
            </p>

            <p className="text-lg">
              I thought the path meant chasing everything—drugs, sex, rock & roll—filling the void with noise and motion. But excess didn't lead to wisdom; it only widened the cracks in my already fractured soul, letting everything drain out faster and faster. I was a broken chalice trying to hold an ocean, never understanding that suffering isn't the same as learning.
            </p>

            <p className="text-lg">
              Then you walked into my life.
            </p>

            <p className="text-lg">
              Your love, your devotion, your beautiful quirky spirit, your relentless bravery and resilience—they've rewritten everything I thought I knew. You've shown me that the wisdom I was searching for wasn't hiding at the bottom of excess. It was waiting in the sanctuary of enough. And your love? It's not just enough—it's more than enough. It's overflowing.
            </p>

            <p className="text-lg">
              You've done something I thought impossible: you keep this broken, leaky, cracked chalice of a soul perpetually filled. Where I expected to keep draining away, you've shown me that love isn't measured by what the vessel can hold, but by the constancy of what pours in.
            </p>

            <p className="text-lg">
              I can't find words vast enough to express my gratitude for this fellowship we share, for the redemption I feel in your presence, for the acceptance you give so freely. You've taught me that wisdom wasn't waiting at the end of excess—it was waiting in the abundance of you.
            </p>

            <p className="text-xl font-serif text-pink-400 text-center mt-12">
              Forever yours,<br />
              Jagiya<br />
              E. H.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Full-Screen Image Viewer
const ImageViewer = ({ isOpen, image, images, currentIndex, onClose, onNavigate }) => {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      
      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          onNavigate('prev');
          break;
        case 'ArrowRight':
          e.preventDefault();
          onNavigate('next');
          break;
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose, onNavigate]);

  if (!isOpen || !image) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/90 backdrop-blur-sm"
        onClick={(e) => {
          e.preventDefault();
          onClose();
        }}
      />
      
      {/* Navigation */}
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          onNavigate('prev');
        }}
        className="absolute left-2 lg:left-4 top-1/2 -translate-y-1/2 glass p-3 lg:p-4 rounded-full hover:bg-white/20 transition-colors z-10"
      >
        <ChevronLeft size={24} className="lg:w-8 lg:h-8" />
      </button>
      
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          onNavigate('next');
        }}
        className="absolute right-2 lg:right-4 top-1/2 -translate-y-1/2 glass p-3 lg:p-4 rounded-full hover:bg-white/20 transition-colors z-10"
      >
        <ChevronRight size={24} className="lg:w-8 lg:h-8" />
      </button>
      
      {/* Close Button */}
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          onClose();
        }}
        className="absolute top-2 lg:top-4 right-2 lg:right-4 glass p-2 lg:p-3 rounded-full hover:bg-white/20 transition-colors z-10"
      >
        <X size={20} className="lg:w-6 lg:h-6" />
      </button>
      
      {/* Image and Details */}
      <div className="relative max-w-7xl max-h-[90vh] w-full mx-4 flex flex-col lg:flex-row gap-4 lg:gap-6">
        {/* Image */}
        <div className="flex-1 flex items-center justify-center min-h-0">
          <img
            src={image.src}
            alt={image.caption}
            className="max-w-full max-h-full object-contain rounded-xl shadow-2xl"
          />
        </div>
        
        {/* Details Panel */}
        <div className="lg:w-96 glass rounded-xl p-4 lg:p-6 space-y-4 overflow-y-auto max-h-48 lg:max-h-none">
          <div>
            <h3 className="text-lg lg:text-xl font-serif font-bold text-pink-400 mb-2">
              {image.caption}
            </h3>
            <p className="text-sm text-white/60">
              {currentIndex + 1} of {images.length}
            </p>
          </div>
          
          {image.prompt && (
            <div>
              <h4 className="text-lg font-semibold text-white mb-2">Creation Details</h4>
              <p className="text-sm text-white/80 leading-relaxed">
                {image.prompt}
              </p>
            </div>
          )}
          
          <div className="pt-4 border-t border-white/20">
            <p className="text-xs text-white/60">
              Use arrow keys or buttons to navigate • Press ESC to close
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Enhanced Image Carousel
const ImageCarousel = ({ images, title }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  }, [images.length]);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  const goToImage = (index) => setCurrentIndex(index);

  const openImageViewer = (image, index) => {
    setSelectedImage(image);
    setSelectedImageIndex(index);
    setImageViewerOpen(true);
  };

  const navigateViewer = (direction) => {
    const newIndex = direction === 'next' 
      ? (selectedImageIndex + 1) % images.length
      : (selectedImageIndex - 1 + images.length) % images.length;
    
    setSelectedImageIndex(newIndex);
    setSelectedImage(images[newIndex]);
  };

  // Auto-play effect
  useEffect(() => {
    const interval = setInterval(goToNext, 5000);
    return () => clearInterval(interval);
  }, [goToNext]);

  // Determine if this is inspirational messages gallery (contains text that needs to be fully visible)
  const isInspirationalMessages = title === "Inspirational Messages";
  const imageObjectFit = isInspirationalMessages ? "object-contain" : "object-cover";
  const backgroundClass = isInspirationalMessages ? "bg-gradient-to-br from-pink-900/20 to-purple-900/20" : "";
  const containerClass = isInspirationalMessages ? "inspirational-image-container" : "aspect-[4/3]";

  return (
    <>
      <div className="relative group">
        {/* Main Image */}
        <div className={`relative ${containerClass} rounded-2xl overflow-hidden shadow-2xl premium-hover animate-hover-glow animate-premium-shimmer ${backgroundClass}`}>
          <img
            src={images[currentIndex].src}
            alt={images[currentIndex].caption}
            className={`w-full h-full ${imageObjectFit} cursor-pointer hover:scale-105 transition-transform duration-300 animate-warm-strobe ${isInspirationalMessages ? 'inspirational-image' : ''}`}
            onClick={() => openImageViewer(images[currentIndex], currentIndex)}
          />
          
          {/* Gradient Overlay - lighter for inspirational messages to preserve text readability */}
          <div className={`absolute inset-0 ${isInspirationalMessages ? 'bg-gradient-to-t from-black/30 via-transparent to-transparent' : 'bg-gradient-to-t from-black/60 via-transparent to-transparent'}`}></div>
          
          {/* Caption */}
          <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
            <p className="text-xl font-serif">{images[currentIndex].caption}</p>
            <p className="text-sm opacity-80 mt-1">{currentIndex + 1} of {images.length}</p>
          </div>
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={goToPrevious}
          className="absolute left-4 top-1/2 -translate-y-1/2 glass p-3 rounded-full opacity-0 group-hover:opacity-100 transition-all hover-scale"
          aria-label="Previous image"
        >
          <ChevronLeft className="w-6 h-6 text-white" />
        </button>
        <button
          onClick={goToNext}
          className="absolute right-4 top-1/2 -translate-y-1/2 glass p-3 rounded-full opacity-0 group-hover:opacity-100 transition-all hover-scale"
          aria-label="Next image"
        >
          <ChevronRight className="w-6 h-6 text-white" />
        </button>

        {/* Thumbnail Navigation */}
        <div className="mt-4 flex gap-2 overflow-x-auto pb-2 scrollbar-thin">
          {images.map((img, index) => (
            <button
              key={index}
              onClick={() => goToImage(index)}
              className={`relative flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden transition-all premium-hover ${backgroundClass} ${
                index === currentIndex 
                  ? 'ring-4 ring-pink-500 scale-110 animate-hover-glow' 
                  : 'opacity-60 hover:opacity-100'
              }`}
            >
              <img 
                src={img.src} 
                alt={`Thumbnail ${index + 1}`} 
                className={`w-full h-full ${imageObjectFit} cursor-pointer animate-cold-strobe`}
                onClick={(e) => {
                  e.stopPropagation();
                  openImageViewer(img, index);
                }}
              />
            </button>
          ))}
        </div>
      </div>

      {/* Image Viewer */}
      <ImageViewer
        isOpen={imageViewerOpen}
        image={selectedImage}
        images={images}
        currentIndex={selectedImageIndex}
        onClose={() => setImageViewerOpen(false)}
        onNavigate={navigateViewer}
      />
    </>
  );
};

// Main App Component
const MiaBelovedApp = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [navbarBlur, setNavbarBlur] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [modalOpen, setModalOpen] = useState(false);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [heartClickCount, setHeartClickCount] = useState(0);
  const [currentVideoId, setCurrentVideoId] = useState('ypU2Uam-S7A');
  const [currentVideoTitle, setCurrentVideoTitle] = useState('');
  const [currentVideoDescription, setCurrentVideoDescription] = useState('');
  
  // Image viewer states
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [currentViewerImage, setCurrentViewerImage] = useState(null);
  const [currentViewerIndex, setCurrentViewerIndex] = useState(0);
  const [allImages, setAllImages] = useState([]);
  
  // Easter egg countdown states
  const [showMainHint, setShowMainHint] = useState(false);
  const [mainHintText, setMainHintText] = useState('');
  const [footerHeart1Count, setFooterHeart1Count] = useState(0);
  const [footerHeart2Count, setFooterHeart2Count] = useState(0);
  const [footerHeart3Count, setFooterHeart3Count] = useState(0);
  const [showFooterHint1, setShowFooterHint1] = useState(false);
  const [showFooterHint2, setShowFooterHint2] = useState(false);
  const [showFooterHint3, setShowFooterHint3] = useState(false);
  const [footerHint1Text, setFooterHint1Text] = useState('');
  const [footerHint2Text, setFooterHint2Text] = useState('');
  const [footerHint3Text, setFooterHint3Text] = useState('');
  
  // Click pulse effects
  const [mainPulse, setMainPulse] = useState(false);
  const [footerPulse1, setFooterPulse1] = useState(false);
  const [footerPulse2, setFooterPulse2] = useState(false);
  const [footerPulse3, setFooterPulse3] = useState(false);
  
  // Floating Hearts Game state
  const [gameActive, setGameActive] = useState(false);
  
  // Hero heart Easter egg states (separate from header heart)
  const [heroHeartClickCount, setHeroHeartClickCount] = useState(0);
  const [showHeroHint, setShowHeroHint] = useState(false);
  const [heroHintText, setHeroHintText] = useState('');
  const [heroPulse, setHeroPulse] = useState(false);
  
  const dailyHighlights = getDailyHighlights();

  // Scroll effect for navbar
  useEffect(() => {
    const handleScroll = () => {
      setNavbarBlur(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Intersection observer for active section
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { threshold: 0.3 }
    );

    document.querySelectorAll('section[id]').forEach((section) => {
      observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  // Main Easter egg hint timer
  useEffect(() => {
    if (showMainHint) {
      const timer = setTimeout(() => {
        setShowMainHint(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [showMainHint]);

  // Footer heart hint timers
  useEffect(() => {
    if (showFooterHint1) {
      const timer = setTimeout(() => setShowFooterHint1(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [showFooterHint1]);

  useEffect(() => {
    if (showFooterHint2) {
      const timer = setTimeout(() => setShowFooterHint2(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [showFooterHint2]);

  useEffect(() => {
    if (showFooterHint3) {
      const timer = setTimeout(() => setShowFooterHint3(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [showFooterHint3]);

  // Reset counters after inactivity
  useEffect(() => {
    if (heartClickCount > 0) {
      const resetTimer = setTimeout(() => {
        setHeartClickCount(0);
      }, 5000);
      return () => clearTimeout(resetTimer);
    }
  }, [heartClickCount]);

  useEffect(() => {
    if (footerHeart1Count > 0) {
      const resetTimer = setTimeout(() => setFooterHeart1Count(0), 5000);
      return () => clearTimeout(resetTimer);
    }
  }, [footerHeart1Count]);

  useEffect(() => {
    if (footerHeart2Count > 0) {
      const resetTimer = setTimeout(() => setFooterHeart2Count(0), 5000);
      return () => clearTimeout(resetTimer);
    }
  }, [footerHeart2Count]);

  useEffect(() => {
    if (footerHeart3Count > 0) {
      const resetTimer = setTimeout(() => setFooterHeart3Count(0), 5000);
      return () => clearTimeout(resetTimer);
    }
  }, [footerHeart3Count]);

  // Hero heart hint timer
  useEffect(() => {
    if (showHeroHint) {
      const timer = setTimeout(() => setShowHeroHint(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [showHeroHint]);

  // Hero heart reset timer
  useEffect(() => {
    if (heroHeartClickCount > 0) {
      const resetTimer = setTimeout(() => setHeroHeartClickCount(0), 5000);
      return () => clearTimeout(resetTimer);
    }
  }, [heroHeartClickCount]);

  // Pulse effect timers
  useEffect(() => {
    if (mainPulse) {
      const timer = setTimeout(() => setMainPulse(false), 600);
      return () => clearTimeout(timer);
    }
  }, [mainPulse]);

  useEffect(() => {
    if (footerPulse1) {
      const timer = setTimeout(() => setFooterPulse1(false), 600);
      return () => clearTimeout(timer);
    }
  }, [footerPulse1]);

  useEffect(() => {
    if (footerPulse2) {
      const timer = setTimeout(() => setFooterPulse2(false), 600);
      return () => clearTimeout(timer);
    }
  }, [footerPulse2]);

  useEffect(() => {
    if (footerPulse3) {
      const timer = setTimeout(() => setFooterPulse3(false), 600);
      return () => clearTimeout(timer);
    }
  }, [footerPulse3]);

  useEffect(() => {
    if (heroPulse) {
      const timer = setTimeout(() => setHeroPulse(false), 600);
      return () => clearTimeout(timer);
    }
  }, [heroPulse]);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  const handleHeartClick = (e) => {
    e.preventDefault();
    const newCount = heartClickCount + 1;
    setHeartClickCount(newCount);
    setMainPulse(true); // Trigger pulse effect
    
    if (newCount === 1) {
      setMainHintText('2 more for aespa... 💫');
      setShowMainHint(true);
    } else if (newCount === 2) {
      setMainHintText('1 more for Whiplash... �');
      setShowMainHint(true);
    } else if (newCount === 3) {
      setMainHintText('aespa awaits! ✨');
      setShowMainHint(true);
      setTimeout(() => {
        setCurrentVideoId('jWQx2f-CErU'); // aespa Whiplash MV
        setCurrentVideoTitle("aespa 에스파 'Whiplash' MV");
        setCurrentVideoDescription('');
        setVideoModalOpen(true);
        setHeartClickCount(0);
        setShowMainHint(false);
      }, 800);
    }
    
    scrollToSection('home');
  };

  const handleGameComplete = () => {
    setGameActive(false);
    setTimeout(() => {
      setCurrentVideoId('bGU8GtYX9vw'); // Nightcore - I Wanna Be Your Slave
      setCurrentVideoTitle('Nightcore - I Wanna Be Your Slave (Female Version) (Lyrics)');
      setCurrentVideoDescription('');
      setVideoModalOpen(true);
    }, 800);
  };

  // Game exit handler
  const handleGameExit = () => {
    setGameActive(false);
  };

  // Hero heart Easter Egg handler (separate from header heart)
  const handleHeroHeartClick = (e) => {
    e.preventDefault();
    const newCount = heroHeartClickCount + 1;
    setHeroHeartClickCount(newCount);
    setHeroPulse(true); // Trigger pulse effect
    
    if (newCount === 1) {
      setHeroHintText('2 more for mini-game... 🎮');
      setShowHeroHint(true);
    } else if (newCount === 2) {
      setHeroHintText('1 more for challenge... 🎯');
      setShowHeroHint(true);
    } else if (newCount === 3) {
      setHeroHintText('Let the games begin! 🌟');
      setShowHeroHint(true);
      setTimeout(() => {
        setGameActive(true);
        setHeroHeartClickCount(0);
        setShowHeroHint(false);
      }, 800);
    }
  };

  // Footer heart Easter Egg handlers
  const handleFooterHeart1Click = (e) => {
    e.preventDefault();
    const newCount = footerHeart1Count + 1;
    setFooterHeart1Count(newCount);
    setFooterPulse1(true); // Trigger pulse effect
    
    if (newCount === 1) {
      setFooterHint1Text('2 more for Bruce Lee... 🥋');
      setShowFooterHint1(true);
    } else if (newCount === 2) {
      setFooterHint1Text('1 more for nunchucks... 👊�');
      setShowFooterHint1(true);
    } else if (newCount === 3) {
      setFooterHint1Text('Watch the moves! 💫');
      setShowFooterHint1(true);
      setTimeout(() => {
        setCurrentVideoId('WqqcrBtz00w'); // Bruce Lee nunchucks dance
        setCurrentVideoTitle("Hittin' like Bruce Lee with the nunchucks 👊🏽");
        setCurrentVideoDescription("#stupid #yg");
        setVideoModalOpen(true);
        setFooterHeart1Count(0);
        setShowFooterHint1(false);
      }, 800);
    }
  };

  const handleFooterHeart2Click = (e) => {
    e.preventDefault();
    const newCount = footerHeart2Count + 1;
    setFooterHeart2Count(newCount);
    setFooterPulse2(true); // Trigger pulse effect
    
    if (newCount === 1) {
      setFooterHint2Text('2 more for love... 💕');
      setShowFooterHint2(true);
    } else if (newCount === 2) {
      setFooterHint2Text('1 more for melody... 🎼');
      setShowFooterHint2(true);
    } else if (newCount === 3) {
      setFooterHint2Text('Little Lover awaits! 🌟');
      setShowFooterHint2(true);
      setTimeout(() => {
        setCurrentVideoId('hFwE9T3mNVI'); // The Official Hella Saucy Dance Video
        setCurrentVideoTitle('The Official Hella Saucy Dance Video ! (Yiking + ALL IN)');
        setCurrentVideoDescription('Priceless Da ROC | TPE | Turf Feinz');
        setVideoModalOpen(true);
        setFooterHeart2Count(0);
        setShowFooterHint2(false);
      }, 800);
    }
  };

  const handleFooterHeart3Click = (e) => {
    e.preventDefault();
    const newCount = footerHeart3Count + 1;
    setFooterHeart3Count(newCount);
    setFooterPulse3(true); // Trigger pulse effect
    
    if (newCount === 1) {
      setFooterHint3Text('2 more for mystery... 🦊');
      setShowFooterHint3(true);
    } else if (newCount === 2) {
      setFooterHint3Text('1 more for the fox... 🌙');
      setShowFooterHint3(true);
    } else if (newCount === 3) {
      setFooterHint3Text('What does it say? 🎭');
      setShowFooterHint3(true);
      setTimeout(() => {
        setCurrentVideoId('jofNR_WkoCE'); // What does the Fox Say
        setVideoModalOpen(true);
        setFooterHeart3Count(0);
        setShowFooterHint3(false);
      }, 800);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-rose-900 text-white font-sans bg-particles">
      <FloatingHearts />
      <CupidArrows />

      {/* Sticky Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        navbarBlur ? 'glass-strong shadow-2xl' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <button type="button" onClick={handleHeartClick} className="flex items-center gap-2 group relative">
              <div className="relative">
                <Heart className={`w-8 h-8 text-pink-400 animate-heartbeat transition-all duration-300 ${
                  heartClickCount > 0 ? 'scale-110 text-pink-300 drop-shadow-lg' : ''
                } ${mainPulse ? 'animate-click-pulse' : ''}`} />
                {/* Easter Egg Click Hint */}
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full animate-ping opacity-75"></div>
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-300 rounded-full animate-pulse"></div>
                
                {/* Countdown Hint */}
                {showMainHint && (
                  <div className={`absolute -bottom-8 left-1/2 transform -translate-x-1/2 
                    glass-tooltip text-white text-xs px-3 py-2 rounded-lg whitespace-nowrap
                    animate-fade-in-out drop-shadow-lg
                    ${showMainHint ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}>
                    <span className="drop-shadow-md">{mainHintText}</span>
                  </div>
                )}
              </div>
              <span className="text-2xl font-serif font-bold text-gradient">
                <span className="inline-block relative ornate-border p-1">
                  <span className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-pink-400 via-rose-300 to-pink-400 bg-clip-text text-transparent animate-pulse-slow drop-shadow-lg">
                    M
                  </span>
                  <span className="text-2xl md:text-3xl text-white/90 font-light tracking-wide mx-1">ia</span>
                  <span className="text-2xl md:text-3xl text-white/80 font-light">Beloved</span>
                  <span className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-pink-400 via-rose-300 to-pink-400 bg-clip-text text-transparent animate-pulse-slow drop-shadow-lg">
                    M
                  </span>
                  <span className="text-2xl md:text-3xl text-white/90 font-light tracking-wide mx-1">ausi</span>
                </span>
              </span>

            </button>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              {[
                { id: 'home', label: 'Home' },
                { id: 'love-letter', label: 'My Devotion' },
                { id: 'highlights', label: 'Today' },
                { id: 'galleries', label: 'Galleries' }
              ].map(({ id, label }) => (
                <button
                  key={id}
                  onClick={() => scrollToSection(id)}
                  className={`transition-all hover:text-pink-300 ${
                    activeSection === id ? 'text-pink-400 font-semibold' : 'text-white/80'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden glass p-2 rounded-lg"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 glass rounded-xl p-6 space-y-4 animate-fade-in-up">
              {[
                { id: 'home', label: 'Home' },
                { id: 'love-letter', label: 'My Devotion' },
                { id: 'highlights', label: 'Today' },
                { id: 'galleries', label: 'Galleries' }
              ].map(({ id, label }) => (
                <button
                  key={id}
                  onClick={() => scrollToSection(id)}
                  className={`block w-full text-left py-2 transition-all hover:text-pink-300 ${
                    activeSection === id ? 'text-pink-400 font-semibold' : 'text-white/80'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center px-6 pt-20">
        <div className="max-w-4xl mx-auto text-center space-y-8 animate-fade-in-up">
          <div className="relative">
            <Heart 
              className={`w-24 h-24 mx-auto text-pink-400 animate-heartbeat cursor-pointer transition-all duration-300 hover:scale-110 ${
                heroHeartClickCount > 0 ? 'scale-110 text-pink-300 drop-shadow-lg' : ''
              } ${heroPulse ? 'animate-click-pulse' : ''}`}
              onClick={handleHeroHeartClick}
            />
            
            {/* Hero Heart Hint */}
            {showHeroHint && (
              <div className={`absolute -bottom-12 left-1/2 transform -translate-x-1/2 
                glass-tooltip text-white text-sm px-4 py-3 rounded-xl whitespace-nowrap
                drop-shadow-lg z-50
                ${showHeroHint ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}>
                <span className="drop-shadow-md">{heroHintText}</span>
              </div>
            )}
          </div>
          <div className="space-y-4">
            <h1 className="text-6xl md:text-8xl font-serif leading-tight">
              <span className="inline-block relative ornate-border p-4">
                <span className="block text-center font-bold bg-gradient-to-r from-pink-400 via-rose-300 to-pink-400 bg-clip-text text-transparent animate-pulse-slow drop-shadow-lg animate-glow">
                  M
                </span>
                <span className="block text-4xl md:text-6xl mt-4 text-white/90 font-light tracking-wide leading-tight">Mia Beloved</span>
                <span className="block text-center font-bold bg-gradient-to-r from-pink-400 via-rose-300 to-pink-400 bg-clip-text text-transparent animate-pulse-slow drop-shadow-lg animate-glow mt-4">
                  M
                </span>
                <span className="block text-4xl md:text-6xl mt-4 text-white/90 font-light tracking-wide leading-tight">Mausi</span>
              </span>
            </h1>
            <div className="w-48 h-1 mx-auto bg-gradient-to-r from-transparent via-pink-400 to-transparent animate-pulse-slow animate-shimmer mt-8 rounded-full"></div>
          </div>
          <p className="text-2xl md:text-3xl text-white/90 font-light">
            A Digital Testament of Love & Devotion
          </p>
          <p className="text-lg md:text-xl text-white/70 max-w-2xl mx-auto leading-relaxed">
            Every image, every word, every pixel crafted with endless love. 
            Welcome to our sanctuary of memories and dreams.
          </p>
          <button
            onClick={() => setModalOpen(true)}
            className="glass px-8 py-4 rounded-full text-lg font-semibold hover-scale transition-all inline-flex items-center gap-2"
          >
            Read My Love Letter <Heart className="w-5 h-5 animate-heartbeat" />
          </button>
        </div>
      </section>

      {/* Love Letter Section */}
      <section id="love-letter" className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="glass rounded-3xl p-12 space-y-8 shadow-2xl animate-fade-in-up">
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-center text-gradient mb-12">
              My Dearest Mia
            </h2>
            
            <div className="prose prose-lg prose-invert max-w-none space-y-6 text-white/90 leading-relaxed">
              <p className="text-xl">
                In this digital sanctuary, I&apos;ve gathered more than just images—I&apos;ve captured the essence 
                of what you mean to me. Every warrior&apos;s stance represents my determination to be worthy 
                of your love. Every golden hour sunset mirrors the warmth you bring to my life.
              </p>

              <p className="text-lg">
                The fantasy scenes aren&apos;t just beautiful art—they&apos;re windows into how magical you make 
                every ordinary moment feel. When I look at the enchanted springs, I see the peace and 
                serenity you bring to my chaotic world. Each hopeful moment frozen in time represents 
                my promise to always cherish and protect what we have.
              </p>

              <p className="text-lg">
                I built this with modern technology because you deserve something as current and vibrant 
                as our love. React, Vite, Tailwind—these tools helped me create something that loads 
                instantly, looks stunning on any device, and feels as smooth as your touch.
              </p>

              <p className="text-lg">
                This isn&apos;t just a gallery. It&apos;s a promise. A testament. A digital love letter that will 
                exist as long as servers run and code compiles. You can visit this on sleepless nights, 
                on difficult days, whenever you need to be reminded: you are loved beyond measure, 
                cherished beyond words, and treasured beyond all the technology in the world.
              </p>

              <p className="text-xl font-serif text-pink-400 text-center mt-12">
                Forever yours, with all the love code can express and more
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Daily Highlights Section */}
      <section id="highlights" className="py-20 px-6 bg-black/20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 animate-fade-in-up">
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-gradient mb-4">
              Today&apos;s Highlights
            </h2>
            <p className="text-xl text-white/70">
              Special moments selected for {new Date().toLocaleDateString('en-US', { 
                weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
              })}
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Featured Image */}
            <div className="space-y-6">
              <h3 className="text-2xl font-serif font-bold text-pink-400">Featured Today</h3>
              <div 
                className="relative aspect-video rounded-xl overflow-hidden shadow-2xl premium-hover animate-hover-glow animate-premium-shimmer group cursor-pointer"
                onClick={(e) => {
                  e.preventDefault();
                  // Use the existing ImageViewer component
                  const allImages = Object.values(galleries).flatMap(g => g.images);
                  const imageIndex = allImages.findIndex(img => img.src === dailyHighlights.featured.src);
                  if (imageIndex !== -1) {
                    setCurrentViewerImage(dailyHighlights.featured);
                    setCurrentViewerIndex(imageIndex);
                    setAllImages(allImages);
                    setImageViewerOpen(true);
                  }
                }}
              >
                <img 
                  src={dailyHighlights.featured.src} 
                  alt={dailyHighlights.featured.caption}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 animate-cold-strobe"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-all flex items-end">
                  <p className="p-6 text-white font-serif text-lg">{dailyHighlights.featured.caption}</p>
                </div>
              </div>
            </div>

            {/* Weekly Carousel */}
            <div className="space-y-6">
              <h3 className="text-2xl font-serif font-bold text-pink-400">This Week's Collection</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {dailyHighlights.weekly.map((image, index) => (
                  <div 
                    key={index}
                    className="relative aspect-square rounded-lg overflow-hidden shadow-xl premium-hover animate-hover-glow cursor-pointer group"
                    onClick={(e) => {
                      e.preventDefault();
                      // Use the existing ImageViewer component
                      const allImages = Object.values(galleries).flatMap(g => g.images);
                      const imageIndex = allImages.findIndex(img => img.src === image.src);
                      if (imageIndex !== -1) {
                        setCurrentViewerImage(image);
                        setCurrentViewerIndex(imageIndex);
                        setAllImages(allImages);
                        setImageViewerOpen(true);
                      }
                    }}
                  >
                    <img 
                      src={image.src} 
                      alt={image.caption} 
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500 animate-warm-strobe" 
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300">
                      <div className="absolute bottom-2 left-2 right-2">
                        <p className="text-xs text-white font-medium">
                          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][index] || 'Sun'}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Galleries Section */}
      <section id="galleries" className="py-20 px-6">
        <div className="max-w-7xl mx-auto space-y-24">
          <div className="text-center animate-fade-in-up">
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-gradient mb-4">
              Our Gallery of Love
            </h2>
            <p className="text-xl text-white/70 max-w-2xl mx-auto">
              104 carefully curated images across 8 themed collections, each telling our story
            </p>
          </div>

          {Object.entries(galleries).map(([key, gallery]) => (
            <div key={key} className="animate-fade-in-up">
              <div className="mb-8">
                <h3 className="text-3xl md:text-4xl font-serif font-bold text-pink-400 mb-2">
                  {gallery.title}
                </h3>
                <p className="text-lg text-white/70">{gallery.description}</p>
              </div>
              <ImageCarousel images={gallery.images} title={gallery.title} />
            </div>
          ))}
        </div>
      </section>

      {/* Enhanced Footer */}
      <footer className="py-16 px-6 bg-gradient-to-b from-black/30 to-black/60 backdrop-blur-xl border-t border-white/10">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-12 mb-12">
            {/* Development Team */}
            <div className="space-y-4">
              <h3 className="text-xl font-serif font-bold text-pink-400">Development Team</h3>
              <div className="space-y-3">
                <a 
                  href="https://github.com/StrayDogSyn" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-white/80 hover:text-pink-300 transition-colors"
                >
                  <ExternalLink size={16} />
                  <span>Jagiya (StrayDogSyn)</span>
                </a>
                <p className="text-sm text-white/60">Lead Developer, Architect</p>
                
                <a 
                  href="https://github.com/miasmith81" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-white/80 hover:text-pink-300 transition-colors"
                >
                  <ExternalLink size={16} />
                  <span>Mausi (miasmith81)</span>
                </a>
                <p className="text-sm text-white/60">Creative Director, Content Curator</p>
              </div>
            </div>

            {/* Company */}
            <div className="space-y-4">
              <h3 className="text-xl font-serif font-bold text-pink-400">Organization</h3>
              <a 
                href="https://www.straydog-syndications-llc.com/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-white/80 hover:text-pink-300 transition-colors"
              >
                <ExternalLink size={16} />
                <span>StrayDog Syndications LLC</span>
              </a>
              <p className="text-sm text-white/60">Digital Innovation & Creative Solutions</p>
            </div>

            {/* Tech Stack */}
            <div className="space-y-4">
              <h3 className="text-xl font-serif font-bold text-pink-400">Tech Stack</h3>
              <div className="space-y-2 text-sm text-white/70">
                <p>React 18.3 with modern hooks</p>
                <p>Vite 5.3 for lightning-fast builds</p>
                <p>Tailwind CSS 3.4 with custom animations</p>
                <p>Lucide React for beautiful icons</p>
              </div>
            </div>
          </div>

          {/* Bottom Section */}
          <div className="text-center space-y-4 pt-8 border-t border-white/10">
            <div className="flex items-center justify-center gap-2 relative">
              {/* Heart 1 - Truth Is - Tweaker */}
              <button 
                type="button"
                onClick={handleFooterHeart1Click}
                className="group relative cursor-pointer hover:scale-110 transition-all duration-300"
              >
                <Heart className={`w-6 h-6 text-pink-400 animate-heartbeat hover:text-pink-300 transition-all duration-300 ${
                  footerHeart1Count > 0 ? 'scale-125 text-pink-300 drop-shadow-lg' : ''
                } ${footerPulse1 ? 'animate-click-pulse' : ''}`} />
                
                {/* Countdown Hint */}
                {showFooterHint1 && (
                  <div className={`absolute -top-8 left-1/2 transform -translate-x-1/2 
                    glass-tooltip text-white text-xs px-3 py-2 rounded-lg whitespace-nowrap
                    drop-shadow-lg z-50
                    ${showFooterHint1 ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}>
                    <span className="drop-shadow-md">{footerHint1Text}</span>
                  </div>
                )}
              </button>
              
              {/* Heart 2 - Little Lover - 40 Below Summer */}
              <button 
                type="button"
                onClick={handleFooterHeart2Click}
                className="group relative cursor-pointer hover:scale-110 transition-all duration-300"
              >
                <Heart className={`w-8 h-8 text-pink-500 animate-heartbeat hover:text-pink-400 transition-all duration-300 ${
                  footerHeart2Count > 0 ? 'scale-125 text-pink-400 drop-shadow-lg' : ''
                } ${footerPulse2 ? 'animate-click-pulse' : ''}`} style={{ animationDelay: '0.3s' }} />
                
                {/* Countdown Hint */}
                {showFooterHint2 && (
                  <div className={`absolute -top-8 left-1/2 transform -translate-x-1/2 
                    glass-tooltip text-white text-xs px-3 py-2 rounded-lg whitespace-nowrap
                    drop-shadow-lg z-50
                    ${showFooterHint2 ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}>
                    <span className="drop-shadow-md">{footerHint2Text}</span>
                  </div>
                )}
              </button>
              
              {/* Heart 3 - What does the Fox Say */}
              <button 
                type="button"
                onClick={handleFooterHeart3Click}
                className="group relative cursor-pointer hover:scale-110 transition-all duration-300"
              >
                <Heart className={`w-6 h-6 text-pink-400 animate-heartbeat hover:text-pink-300 transition-all duration-300 ${
                  footerHeart3Count > 0 ? 'scale-125 text-pink-300 drop-shadow-lg' : ''
                } ${footerPulse3 ? 'animate-click-pulse' : ''}`} style={{ animationDelay: '0.6s' }} />
                
                {/* Countdown Hint */}
                {showFooterHint3 && (
                  <div className={`absolute -top-8 left-1/2 transform -translate-x-1/2 
                    glass-tooltip text-white text-xs px-3 py-2 rounded-lg whitespace-nowrap
                    drop-shadow-lg z-50
                    ${showFooterHint3 ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}>
                    <span className="drop-shadow-md">{footerHint3Text}</span>
                  </div>
                )}
              </button>
            </div>
            <p className="text-white/60">
              Built with React, Vite, Tailwind CSS, and endless love
            </p>
            <p className="text-white/40 text-sm">
              © {new Date().getFullYear()} • Created by Jagiya for Mausi 💝
            </p>
          </div>
        </div>
      </footer>

      {/* Love Letter Modal */}
      <LoveLetterModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />
      
      {/* Video Modal */}
      <VideoModal
        isOpen={videoModalOpen}
        onClose={() => setVideoModalOpen(false)}
        videoId={currentVideoId}
        customTitle={currentVideoTitle}
        customDescription={currentVideoDescription}
      />
      
      {/* Image Viewer */}
      <ImageViewer
        isOpen={imageViewerOpen}
        image={currentViewerImage}
        images={allImages}
        currentIndex={currentViewerIndex}
        onClose={() => setImageViewerOpen(false)}
        onNavigate={(direction) => {
          const newIndex = direction === 'next'
            ? (currentViewerIndex + 1) % allImages.length
            : (currentViewerIndex - 1 + allImages.length) % allImages.length;
          setCurrentViewerIndex(newIndex);
          setCurrentViewerImage(allImages[newIndex]);
        }}
      />
      
      {/* Floating Hearts Mini-Game */}
      <FloatingHeartsGame
        isActive={gameActive}
        onGameComplete={handleGameComplete}
        onGameExit={handleGameExit}
      />
    </div>
  );
};

export default MiaBelovedApp;
